import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { X } from 'lucide-react';
import ContentLoader from '../ui/ContentLoader';
import ProductForm from './ProductForm';
import { getProduct, createProduct, updateProduct } from '../../services/api';
import { Product, ProductFormData } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';

interface ProductOnboardingModalProps {
  productId?: string;
  isOpen?: boolean;
  onClose: () => void;
  onProductAdded?: () => Promise<void>;
}

const ProductOnboardingModal: React.FC<ProductOnboardingModalProps> = ({
  productId,
  isOpen = true,
  onClose,
  onProductAdded
}) => {
  const { user } = useAuth();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Add backdrop effect to body when modal opens
  useEffect(() => {
    if (isOpen) {
      // Add backdrop blur class to body
      document.body.classList.add('modal-open');
      // Disable scrolling on the body
      document.body.style.overflow = 'hidden';
    } else {
      // Remove backdrop blur class from body
      document.body.classList.remove('modal-open');
      // Re-enable scrolling
      document.body.style.overflow = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  const isEditMode = !!productId;
  
  // Fetch product if editing
  useEffect(() => {
    if (isOpen && isEditMode) {
      fetchProduct(productId);
    }
  }, [isEditMode, productId, isOpen]);
  
  // Fetch product data from API
  const fetchProduct = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getProduct(id);
      setProduct(data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product data');
      setError('Failed to load product data');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (formData: ProductFormData) => {
    setIsSubmitting(true);
    
    // Debug logging to check the structure
    // Product form data before processing
    
    try {
      // Validate that we have integration fields data
      if (!formData.integration_fields || !Array.isArray(formData.integration_fields)) {
        console.error("Missing or invalid integration_fields in form data");
        formData.integration_fields = [];
      }
      
      // Log count of integration fields
              // Processing integration fields
      
      // Check for services field explicitly
      const servicesField = formData.integration_fields.find(f => f.field_key === 'services');
      if (servicesField) {
                  // Found services field with options
        
        // Ensure options array exists
        if (!servicesField.options) {
          servicesField.options = [];
          console.warn("Services field had no options array, created empty array");
        }
              } else {
          // No services field found in input data
        }
      
      // Process each field to ensure correct formatting
      formData.integration_fields = formData.integration_fields.map(field => {
        // Create a proper deep copy
        const formatted = JSON.parse(JSON.stringify(field));
        
                  // Processing field
        
        // Special handling for multi-select fields
        if (field.type === 'multi-select') {
          // Ensure options is an array
          if (!formatted.options) {
            formatted.options = [];
          }
          
          // Special handling for services field
          if (field.field_key === 'services') {
            // Services field options processed
          }
        }
        
        // Special handling for SOAR mapping fields
        if (field.type === 'soar-mapping') {
          // Ensure field_mapping is an object
          if (!formatted.field_mapping) {
            formatted.field_mapping = {};
            console.warn("SOAR mapping field had no field_mapping object, created empty object");
          }
          
                      // SOAR field mapping processed
        }
        
        return formatted;
      });
      
      // Final check for services field
      const finalServicesField = formData.integration_fields.find(f => f.field_key === 'services');
      
      // Final product data prepared for submission
      
      if (isEditMode) {
        // Update existing product
        await updateProduct(productId as string, formData);
        toast.success('Product updated successfully');
      } else {
        // Create new product
        await createProduct(formData);
        toast.success('Product created successfully');
      }
      
      // Close modal and refresh products list
      if (onProductAdded) {
        await onProductAdded();
      }
      onClose();
    } catch (error: any) {
      console.error('Error saving product:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to save product');
      }
      
      setIsSubmitting(false);
      setError('Failed to save product');
    }
  };
  
  // Check if user has permission to manage products using RBAC
  const { canManageProducts } = usePermissions();
  if (!canManageProducts()) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-hidden">
        <div className="bg-background-card rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto animate-modalOpen">
          <div className="flex items-center justify-between p-4 border-b border-border-dark">
            <h2 className="text-xl font-semibold">Product Onboarding</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 text-white"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-center p-8">
              <div className="text-center">
                <h2 className="text-xl font-semibold mb-2">Permission Denied</h2>
                <p className="text-text-muted-dark">
                  You don't have permission to onboard new products.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-hidden">
      <div className="bg-background-card rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto animate-modalOpen">
        <div className="flex items-center justify-between p-4 border-b border-border-dark sticky top-0 bg-background-card z-10">
          <h2 className="text-xl font-semibold">{isEditMode ? 'Edit Product' : 'Add New Product'}</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-white hover:text-white transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6">
          {/* Loading state */}
          {isEditMode && isLoading ? (
            <ContentLoader
              message="Loading product details..."
              size="lg"
              withCard={false}
              minHeight="300px"
            />
          ) : (
            /* Product form */
            <>
              {error && (
                <div className="bg-status-danger/10 border border-status-danger/20 text-status-danger rounded-md p-4 mb-6">
                  <p>{error}</p>
                </div>
              )}
              <ProductForm
                initialProduct={product || undefined}
                onSubmit={handleSubmit}
                isLoading={isSubmitting}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductOnboardingModal; 